// shares the trip booking tab's styles (price rows, Book Now, Need Help)
import '../../tripDetails/tripBookingTab/tripBookingTab.css';
import './campBookingTab.css';
import 'react-dates/lib/css/_datepicker.css';

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import moment from 'moment';
import { ChevronDown, ChevronLeft, ChevronRight, Info, Users, BedDouble, Bath, X } from 'lucide-react';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { readCampStay, readUnitSelection, saveCampStay } from '../../../utils/campStay';
import SelectDateRange from '../../../components/SelectDateRange/SelectDateRange';
import BookingSheet from '../../../components/bookingSheet/BookingSheet';
import CampGuestSelect from './CampGuestSelect';
import StayUnitPrices from './StayUnitPrices';
import { calcReserve, linesLabel, maxGuestsOf, maxRoomsOf, priceStayLines, unitIdOf } from './stayLines';
import { saveStayBooking } from '../../reviewBookingCamp/campReviewFun/stayBookingStore';
import { leftFor, useCampAvailability } from '../../../hooks/useCampAvailability';
import { ReactComponent as Phone } from '../../../assets/icons/trvto_phone.svg';
import { ReactComponent as Message } from '../../../assets/icons/trvto_message.svg';

// same support number as the trip booking tab
const HELP_PHONE = '918088788874';

/* =========================================================
   HELPERS
========================================================= */

// date picker value (moment / string) -> "2026-10-07"
const dayOf = (value) => (value && moment(value).isValid() ? moment(value).format('YYYY-MM-DD') : null);

// units to start with: the ones in the URL (?units=<id>-3.<id>-2, back from
// the review page) that this camp has, capped at what it has — else the first unit
const startSelection = (units, searchParams) => {
    const wanted = readUnitSelection(searchParams);
    const fromUrl = units.reduce((picked, unit) => {
        const count = wanted[unitIdOf(unit)];
        if (count) picked[unitIdOf(unit)] = Math.min(count, maxRoomsOf(unit));
        return picked;
    }, {});
    if (Object.keys(fromUrl).length) return fromUrl;
    const first = unitIdOf(units[0]);
    return first ? { [first]: 1 } : {};
};

const CURRENCY_SYMBOLS = {
    INR: '₹',
    USD: '$',
    AED: 'د.إ',
    EUR: '€',
    GBP: '£'
};

const getCurrencySymbol = (currency) => {
    return CURRENCY_SYMBOLS[currency] || '₹';
};

const formatPrice = (value) => {
    if (value === undefined || value === null || value === '') {
        return '0';
    }

    return Number(value).toLocaleString('en-IN');
};

const getImageUrl = (image) => {
    if (!image) {
        return null;
    }

    if (typeof image === 'string') {
        return image;
    }

    return image?.url || null;
};

const getUnitPrice = (unit) => {
    const pricing = unit?.pricing?.[0];

    if (!pricing) {
        return 0;
    }

    return Number(pricing.actualPrice ?? pricing.basePrice ?? 0);
};

const getUnitStrikePrice = (unit) => {
    const pricing = unit?.pricing?.[0];

    if (!pricing) {
        return 0;
    }

    return Number(pricing.strikePrice ?? pricing.actualPrice ?? pricing.basePrice ?? 0);
};

const getUnitDiscount = (unit) => {
    const pricing = unit?.pricing?.[0];

    if (!pricing) {
        return 0;
    }

    return Number(pricing.discountPercent || 0);
};

const getPriceUnit = (unit) => {
    const pricing = unit?.pricing?.[0];

    if (!pricing) {
        return 'night';
    }

    switch (pricing.priceUnit) {
        case 'per_person':
            return 'person';

        case 'per_unit':
            return 'unit';

        case 'per_bed':
            return 'bed';

        case 'per_package':
            return 'package';

        default:
            return 'night';
    }
};

/* =========================================================
   COMPONENT
========================================================= */

// reviewPath: where "Book Now" goes (the v2 site passes its own review page)
export default function CampBookingTab({
    packData,
    setPackData,
    packDates,
    setLoading,
    isExpanded,
    setIsExpanded,
    reviewPath = '/camp/review-booking'
}) {
    const [searchParams, setSearchParams] = useSearchParams();

    // the search the page opened with (URL, else this session's last camp search)
    const [initialStay] = useState(() => readCampStay(searchParams));
    const hasInitialDates = Boolean(initialStay.checkin && initialStay.checkout);
    const navigate = useNavigate();

    /* =====================================================
       UNITS
    ===================================================== */

    const units = useMemo(() => (Array.isArray(packData?.units) ? packData.units : []), [packData?.units]);

    /* =====================================================
       SELECTED UNITS — any mix, e.g. { tentId: 1, dormId: 2 }
       (unit id -> rooms / tents of it). Starts with the first unit.
    ===================================================== */

    const [selection, setSelection] = useState(() => startSelection(units, searchParams));

    const selectedLines = useMemo(
        () =>
            units
                .filter((unit) => selection[unitIdOf(unit)] > 0)
                .map((unit) => ({ unit, rooms: selection[unitIdOf(unit)] })),
        [units, selection]
    );

    const isMulti = selectedLines.length > 1;

    // header price + guest popup use the first selected unit
    const selectedUnit = selectedLines[0]?.unit || units[0] || null;
    const selectedUnitId = unitIdOf(selectedUnit);

    /* =====================================================
       DATE
    ===================================================== */

    const [checkIn, setCheckIn] = useState(hasInitialDates ? initialStay.checkin : null);

    const [checkOut, setCheckOut] = useState(hasInitialDates ? initialStay.checkout : null);

    /* =====================================================
       GUESTS
    ===================================================== */

    const [adults, setAdults] = useState(initialStay.found ? initialStay.adults : 1);

    const [children, setChildren] = useState(initialStay.found ? initialStay.children : 0);

    // pets: only when the stay allows them (guest popup + server check)
    const [pets, setPets] = useState(initialStay.found ? initialStay.pets : 0);

    const petsAllowed = Boolean(
        packData?.policies?.houseRules?.petsAllowed || packData?.propertyRules?.petsAllowed
    );

    // all rooms / tents selected
    const rooms = selectedLines.reduce((total, line) => total + line.rooms, 0) || 1;

    // one unit selected: the guest popup sets its rooms
    const setRooms = (count) => selectedUnitId && setSelection({ [selectedUnitId]: count });

    /* =====================================================
       AVAILABILITY — free rooms of each unit for the picked dates
       (also read by the "Rooms & units" section). The server checks
       again when the guest pays.
    ===================================================== */

    const { availability, refresh: refreshAvailability } = useCampAvailability(packData?._id, {
        checkin: dayOf(checkIn),
        checkout: dayOf(checkOut),
        pets
    });

    // rooms of a unit that can be picked: free on these dates, else the camp's stock
    const roomsLimit = (unit) => leftFor(availability, unitIdOf(unit)) ?? maxRoomsOf(unit);

    const notBookable = availability?.status === 'ready' && !availability.bookable;

    // "Only 1 Cabin left for these dates — selection updated"
    const [availabilityNote, setAvailabilityNote] = useState('');

    const [checkingAvailability, setCheckingAvailability] = useState(false);

    /* =====================================================
       UI
    ===================================================== */

    const [showUnits, setShowUnits] = useState(false);

    const [showGuests, setShowGuests] = useState(false);

    // back from the review page ("Change Details") with ?reserve=1
    const [isReserveChecked, setIsReserveChecked] = useState(() => searchParams.get('reserve') === '1');

    const bookingCardRef = useRef(null);

    /* =====================================================
       LOAD QUERY PARAMS
    ===================================================== */

    // the query this tab last wrote to the URL (so it isn't read back in)
    const syncedQuery = useRef(null);

    useEffect(() => {
        /*
         * Prefill from the search: URL (?checkin=2026-10-07&checkout=...&adults=2)
         * or this session's last camp search (utils/campStay.js).
         * Also runs when the URL changes from outside (back button, a link).
         */
        if (searchParams.toString() === syncedQuery.current) return;
        const stay = readCampStay(searchParams);

        if (stay.checkin && stay.checkout) {
            setCheckIn(stay.checkin);
            setCheckOut(stay.checkout);
        }

        if (stay.found) {
            setAdults(stay.adults);
            setChildren(stay.children);
            setPets(stay.pets);
        }
    }, [searchParams]);

    // Keep changes made here for the next page (review / checkout)
    useEffect(() => {
        saveCampStay({ checkin: checkIn, checkout: checkOut, adults, children, pets });
    }, [checkIn, checkOut, adults, children, pets]);

    // ...and in the URL, so the search bar, a refresh or a shared link show the
    // same dates and guests (replace: no extra back-button steps)
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        const put = (key, value) => (value ? params.set(key, String(value)) : params.delete(key));
        put('checkin', dayOf(checkIn));
        put('checkout', dayOf(checkOut));
        params.set('adults', String(adults));
        put('children', children);
        put('pets', pets);
        params.delete('guests'); // old links: guests=2-1_2-0
        const next = params.toString();
        if (next === searchParams.toString()) return;
        syncedQuery.current = next;
        setSearchParams(params, { replace: true });
    }, [checkIn, checkOut, adults, children, pets]); // eslint-disable-line react-hooks/exhaustive-deps

    // the search bar's "Where": the place searched, else this camp's city
    // (added whenever the URL lacks it, e.g. after the page's own redirects)
    const searchPlace = initialStay.place || packData?.address?.city || '';
    useEffect(() => {
        if (!searchPlace || searchParams.get('place')) return;
        const params = new URLSearchParams(searchParams);
        params.set('place', searchPlace);
        syncedQuery.current = params.toString();
        setSearchParams(params, { replace: true });
    }, [searchPlace, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    /* =====================================================
       UPDATE DEFAULT UNIT
    ===================================================== */

    // units from the URL (back from the review page), else the first unit
    useEffect(() => {
        if (selectedLines.length || !units.length) return;
        setSelection(startSelection(units, searchParams));
    }, [units, selectedLines.length, searchParams]);

    /* =====================================================
       PRICE INFORMATION
    ===================================================== */

    const pricing = selectedUnit?.pricing?.[0] || null;

    const currencySymbol = getCurrencySymbol(pricing?.currency);

    const priceUnit = getPriceUnit(selectedUnit);

    const currentPrice = getUnitPrice(selectedUnit);

    const strikePrice = getUnitStrikePrice(selectedUnit);

    const discountPercent = getUnitDiscount(selectedUnit);

    /* =====================================================
       NIGHTS
    ===================================================== */

    const nights = useMemo(() => {
        if (!checkIn || !checkOut) {
            return 1;
        }

        const start = moment(checkIn);

        const end = moment(checkOut);

        const difference = end.diff(start, 'days');

        return difference > 0 ? difference : 1;
    }, [checkIn, checkOut]);

    /* =====================================================
       GUEST TOTAL
    ===================================================== */

    const totalGuests = adults + children;

    /* =====================================================
       ROOMS / TENTS OF THE SELECTED UNIT
    ===================================================== */

    const perUnit = maxGuestsOf(selectedUnit);

    const maxRooms = Math.max(roomsLimit(selectedUnit), 1);

    // one unit: enough rooms for everyone, never more than the property has
    // (several units: the customer picks the mix in "View all units")
    useEffect(() => {
        if (selectedLines.length !== 1) return;
        const { unit, rooms: count } = selectedLines[0];
        const next = Math.min(
            Math.max(count, Math.ceil(totalGuests / maxGuestsOf(unit)), 1),
            Math.max(roomsLimit(unit), 1)
        );
        if (next !== count) setSelection({ [unitIdOf(unit)]: next });
    }, [totalGuests, selectedLines, availability]);

    // dates changed: old notes no longer apply
    useEffect(() => setAvailabilityNote(''), [checkIn, checkOut]);

    // fewer free than selected: keep what's free (sold-out units are taken off,
    // a free unit is picked if nothing selected is left) and tell the guest
    useEffect(() => {
        if (availability?.status !== 'ready' || !availability.bookable) return;
        const next = {};
        const changes = [];
        for (const { unit, rooms: count } of selectedLines) {
            const id = unitIdOf(unit);
            const left = leftFor(availability, id);
            if (left >= count) next[id] = count;
            else {
                if (left > 0) next[id] = left;
                changes.push(left > 0 ? `Only ${left} ${unit.name || 'room'} left` : `${unit.name || 'A room'} is sold out`);
            }
        }
        if (!changes.length) return;
        if (!Object.keys(next).length) {
            const free = units.find((unit) => leftFor(availability, unitIdOf(unit)) > 0);
            if (free) next[unitIdOf(free)] = 1;
        }
        setSelection(next);
        setAvailabilityNote(`${changes.join(' · ')} for these dates — we updated your selection.`);
    }, [availability, selectedLines, units]);

    const unitWord = isMulti ? 'Rooms' : selectedUnit?.name || 'Room';

    /* =====================================================
       PRICE CALCULATION
    ===================================================== */

    // every selected unit priced (backend utils/stayPricing.js does the same)
    const priceDetails = useMemo(() => {
        // check-in date: weekend / season / early-bird prices of each night
        const priced = priceStayLines(selectedLines, { guests: totalGuests, nights, checkIn: dayOf(checkIn) });
        return {
            ...priced,
            // one unit: the row reads "₹list × 2 nights × 2"
            listPrice: priced.lines[0]?.listPrice || 0,
            subtotal: priced.base,
            discount: 0 // the "discount" is only shown, never taken off
        };
    }, [selectedLines, totalGuests, nights, checkIn]);

    // the selected units together must fit everyone
    const capacityShort = totalGuests > priceDetails.capacity;

    /* =====================================================
       IMAGE
    ===================================================== */

    const unitImages = useMemo(() => {
        if (!Array.isArray(selectedUnit?.images)) {
            return [];
        }

        return selectedUnit.images.map(getImageUrl).filter(Boolean);
    }, [selectedUnit]);

    const [activeImage, setActiveImage] = useState(0);

    useEffect(() => {
        setActiveImage(0);
    }, [selectedUnitId]);

    /* =====================================================
       UNIT IMAGE NAVIGATION
    ===================================================== */

    const nextImage = () => {
        if (unitImages.length <= 1) {
            return;
        }

        setActiveImage((current) => (current >= unitImages.length - 1 ? 0 : current + 1));
    };

    const previousImage = () => {
        if (unitImages.length <= 1) {
            return;
        }

        setActiveImage((current) => (current <= 0 ? unitImages.length - 1 : current - 1));
    };

    /* =====================================================
       SELECT UNITS — + / − rooms of any unit (at least one unit stays)
    ===================================================== */

    const changeUnitRooms = (unit, delta) => {
        const id = unitIdOf(unit);
        const count = Math.min(Math.max((selection[id] || 0) + delta, 0), roomsLimit(unit));
        const next = { ...selection, [id]: count };
        if (!count) delete next[id];
        if (!Object.keys(next).length) {
            toast.info('Keep at least one unit selected');
            return;
        }
        setSelection(next);
    };

    /* =====================================================
       RESERVE CHECKBOX
    ===================================================== */

    const reserveAmount = priceDetails.reserve;

    const hasReserveOption = reserveAmount > 0;


    /* =====================================================
       BOOK NOW
    ===================================================== */

    const handleBookNow = async () => {
        if (checkingAvailability) return;

        if (!checkIn) {
            toast.error('Please select check-in date');

            return;
        }

        if (!checkOut) {
            toast.error('Please select check-out date');

            return;
        }

        if (!selectedLines.length) {
            toast.error('Please select an accommodation unit');

            return;
        }

        if (capacityShort) {
            toast.error(
                `The selected units fit ${priceDetails.capacity} guests. Please add another unit for ${totalGuests} guests.`
            );
            setShowUnits(true);
            return;
        }

        if (pets > 0 && !petsAllowed) {
            toast.error("Pets aren't allowed at this stay. Please set pets to 0.");
            setShowGuests(true);
            return;
        }

        if (notBookable) {
            toast.error(availability.reason || 'Not available for these dates. Please pick other dates.');
            return;
        }

        // check again — someone may have booked while the page was open
        setCheckingAvailability(true);
        const fresh = await refreshAvailability();
        setCheckingAvailability(false);
        if (fresh) {
            if (!fresh.bookable) {
                toast.error(fresh.reason || 'Not available for these dates. Please pick other dates.');
                return;
            }
            const short = selectedLines.filter(({ unit, rooms: count }) => leftFor(fresh, unitIdOf(unit)) < count);
            if (short.length) {
                toast.error(
                    `${short
                        .map(({ unit }) => {
                            const left = leftFor(fresh, unitIdOf(unit));
                            return left > 0 ? `Only ${left} ${unit.name || 'room'} left` : `${unit.name || 'A room'} is sold out`;
                        })
                        .join(', ')} for these dates. Please check your units.`
                );
                setShowUnits(true);
                return;
            }
        }

        // what the review page needs (the server prices it again when booking)
        const firstLine = priceDetails.lines[0];
        const coverImage =
            (packData?.media || []).find((m) => m?.type !== 'video' && m?.url)?.url || unitImages[0] || '';

        const stay = {
            listingId: packData?._id,
            title: packData?.title,
            location: [packData?.address?.city, packData?.address?.state].filter(Boolean).join(', '),
            image: coverImage,
            // every unit booked; unitId / unitName / rooms = first unit / label / all rooms
            items: priceDetails.lines.map(({ unitId, unitName, rooms: count, guests, quantityLabel }) => ({
                unitId,
                unitName,
                rooms: count,
                guests,
                quantityLabel
            })),
            unitId: firstLine.unitId,
            unitName: isMulti ? linesLabel(priceDetails.lines) : firstLine.unitName,
            rooms,
            checkIn,
            checkOut,
            nights,
            guests: { adults, children, pets },
            isReserved: isReserveChecked && hasReserveOption,
            pricing: {
                priceUnit: isMulti ? 'mixed' : firstLine.priceUnit,
                nightly: firstLine.nightly,
                // one row per unit on the review page
                lines: priceDetails.lines.map(({ unitName, rooms: count, listPrice, listBase, quantityLabel }) => ({
                    unitName,
                    rooms: count,
                    listPrice,
                    listBase,
                    quantityLabel
                })),
                listPrice: priceDetails.listPrice,
                listBase: priceDetails.listBase,
                savings: priceDetails.savings,
                subtotal: priceDetails.subtotal,
                tax: priceDetails.tax,
                total: priceDetails.total,
                reserveAmount,
                quantityLabel: firstLine.quantityLabel
            }
        };

        saveStayBooking(stay);
        navigate(reviewPath, { state: { stay } });
    };

    /* =====================================================
       TOGGLE
    ===================================================== */

    const toggleExpand = () => {
        if (typeof setIsExpanded === 'function') {
            setIsExpanded(!isExpanded);
        }
    };

    /* =====================================================
       EMPTY STATE
    ===================================================== */

    if (!packData) {
        return null;
    }

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <>
            {/* =================================================
                MAIN BOOKING CARD
            ================================================= */}

            <div
                ref={bookingCardRef}
                className={`
                    tripBookingTab
                    accommodationBookingTab
                    ${isExpanded ? 'expanded' : 'collapsed'}
                `}
            >
                {/* =================================================
                    TOP PRICE SECTION

                    KEEPING YOUR EXISTING DESIGN
                ================================================= */}

                <div
                    className='
                        tripBookingPriceDiv
                        accommodationPriceHeader
                    '
                >
                    <div className='accommodationPriceContent'>
                        {/* STRIKE PRICE */}

                        {strikePrice > currentPrice && (
                            <span className='tripBookingActualPrice'>
                                {currencySymbol}
                                {formatPrice(strikePrice)}
                            </span>
                        )}

                        {/* DISCOUNT */}

                        {discountPercent > 0 && (
                            <span className='tripBookingOffer'>
                                {Math.round(discountPercent)}
                                %OFF
                            </span>
                        )}

                        {/* MAIN PRICE */}

                        <div className='accommodationMainPrice'>
                            <span>
                                {currencySymbol} {formatPrice(currentPrice)}
                            </span>

                            <strong> / {priceUnit}</strong>
                        </div>

                        <p className='tripBookingTaxInc'>Excluding all taxes</p>
                    </div>
                </div>

                {/* =================================================
                    BOOKING BODY
                ================================================= */}

                <div
                    className='
                        tripBookingSubTab
                        accommodationBookingBody
                    '
                >
                    {/* =================================================
                        DATES
                    ================================================= */}

                    <div className='accommodationBookingSection'>
                        <h3>Select Date</h3>

                        {/* same calendar as the trip page (components/SelectDateRange) */}
                        <SelectDateRange
                            freeRange
                            minimumNights={packData?.policies?.minStayNights || 1}
                            startDate={checkIn ? moment(checkIn) : null}
                            endDate={checkOut ? moment(checkOut) : null}
                            packData={packData}
                            startPlaceholder='Check-in'
                            endPlaceholder='Check-out'
                            setDates={(start, end) => {
                                setCheckIn(start ? moment(start).format('YYYY-MM-DD') : null);
                                setCheckOut(end ? moment(end).format('YYYY-MM-DD') : null);
                            }}
                        />
                    </div>

                    {/* =================================================
                        GUESTS
                    ================================================= */}

                    <div
                        className='
                            accommodationBookingSection
                            accommodationGuestsSection
                        '
                    >
                        <div className='accommodationSectionTitle'>
                            <h3>Select Guests</h3>

                            <Info size={16} color='#155eef' />
                        </div>

                        <button
                            type='button'
                            className='
                                accommodationGuestButton
                            '
                            onClick={() => setShowGuests(true)}
                        >
                            <Users size={19} />

                            <span>
                                {adults} {adults === 1 ? 'Adult' : 'Adults'}
                                {' · '}
                                {children} {children === 1 ? 'Child' : 'Children'}
                                {pets > 0 && ` · ${pets} ${pets === 1 ? 'Pet' : 'Pets'}`}
                                {' · '}
                                {rooms} {unitWord}
                            </span>

                            <ChevronDown size={19} className={showGuests ? 'rotated' : ''} />
                        </button>

                    </div>

                    {/* GUESTS + ROOMS POPUP (same style as activity / guide) */}
                    <BookingSheet open={showGuests} onClose={() => setShowGuests(false)}>
                        <CampGuestSelect
                            value={{ adults, children, pets, rooms }}
                            petsAllowed={petsAllowed}
                            unitName={unitWord}
                            perUnit={perUnit}
                            maxRooms={maxRooms}
                            price={currentPrice}
                            priceUnit={pricing?.priceUnit}
                            nights={nights}
                            currencySymbol={currencySymbol}
                            formatPrice={formatPrice}
                            // reserve option sits in this popup (same as activity / guide)
                            reserveChecked={isReserveChecked}
                            reserveFor={(guests, count) =>
                                isMulti
                                    ? priceStayLines(selectedLines, { guests, nights, checkIn: dayOf(checkIn) }).reserve
                                    : calcReserve(pricing, { guests, rooms: count, nights })
                            }
                            // several units: rooms are picked in "View all units"
                            multi={isMulti}
                            capacity={priceDetails.capacity}
                            selectionLabel={linesLabel(selectedLines)}
                            totalFor={(guests) =>
                                priceStayLines(selectedLines, { guests, nights, checkIn: dayOf(checkIn) }).base
                            }
                            onApply={(next) => {
                                setAdults(next.adults);
                                setChildren(next.children);
                                setPets(next.pets || 0);
                                // several units: their rooms are set in "View all units"
                                if (!isMulti) setRooms(next.rooms);
                                setIsReserveChecked(Boolean(next.reserve));
                                setShowGuests(false);
                            }}
                        />
                    </BookingSheet>

                    {/* =================================================
                        SELECTED UNIT
                    ================================================= */}

                    <div
                        className='
                            accommodationBookingSection
                        '
                    >
                        <div className='accommodationSectionTitle'>
                            <h3>{isMulti ? 'Selected Units' : 'Selected Unit'}</h3>

                            <button
                                type='button'
                                className='
                                    accommodationChangeButton
                                '
                                onClick={() => setShowUnits(true)}
                            >
                                Change
                            </button>
                        </div>

                        {selectedLines.map(({ unit, rooms: count }) => {
                            const id = unitIdOf(unit);
                            const images = Array.isArray(unit?.images) ? unit.images.map(getImageUrl).filter(Boolean) : [];
                            // the first unit keeps the photo arrows' current photo
                            const photo = id === selectedUnitId ? unitImages[activeImage] : images[0];
                            return (
                                <div key={id} className='accommodationSelectedUnit'>
                                    <div className='accommodationSelectedUnitImage'>
                                        {photo ? <img src={photo} alt={unit.name || 'Accommodation'} /> : <div>🏕️</div>}
                                    </div>

                                    <div className='accommodationSelectedUnitInfo'>
                                        <strong>
                                            {unit.name || 'Accommodation Unit'}
                                            {count > 1 || isMulti ? ` × ${count}` : ''}
                                        </strong>

                                        <span>
                                            {unit?.occupancy?.total || unit?.maximumGuests || 0}
                                            {' Guests · '}
                                            {unit?.beds?.reduce((total, bed) => total + Number(bed?.count || 0), 0) || 0}
                                            {' Beds · '}
                                            {unit?.bathrooms?.count || 0}
                                            {' Bathroom'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}

                        {notBookable ? (
                            <p className='accommodationCapacityWarning'>
                                {availability.reason || 'Not available for these dates.'}
                            </p>
                        ) : (
                            availabilityNote && <p className='accommodationAvailabilityNote'>{availabilityNote}</p>
                        )}

                        {capacityShort && (
                            <p className='accommodationCapacityWarning'>
                                The selected units fit {priceDetails.capacity} guests. Add another unit for {totalGuests}{' '}
                                guests.
                            </p>
                        )}

                        <button
                            type='button'
                            className='
                                accommodationViewUnitsButton
                            '
                            onClick={() => setShowUnits(true)}
                        >
                            {isMulti ? 'Add or change units' : 'View all units'}
                            <ChevronRight size={16} />
                        </button>
                    </div>

                    {/* =================================================
                        PRICE SUMMARY
                    ================================================= */}

                    {isReserveChecked && hasReserveOption ? (
                        // reserving: pay the reserve now, the rest later (like the trip tab)
                        <div className='tripBookPriceDiv'>
                            <div className='tripPriceCal'>
                                <p className='tripBookPrice'>Reserve amount</p>
                                <p className='tripBookPrice'>
                                    {currencySymbol}
                                    {formatPrice(reserveAmount)}
                                </p>
                            </div>
                            <div className='tripBookOfferDiv'>
                                <p className='tripBookOfferText'>Pay later</p>
                                <p className='tripBookOfferText'>
                                    {currencySymbol}
                                    {formatPrice(Math.max(priceDetails.base - reserveAmount, 0))}
                                </p>
                            </div>
                            <div className='tripBookTotalDiv'>
                                <p className='tripBookTotalText'>Total:</p>
                                <p className='tripBookTotalText'>
                                    {currencySymbol}
                                    {formatPrice(reserveAmount)}
                                </p>
                            </div>
                            {priceDetails.tax > 0 && (
                                <p className='accommodationTaxNote'>Taxes are added on the review page</p>
                            )}
                        </div>
                    ) : (
                    <div className='tripBookPriceDiv'>
                        {isMulti ? (
                            // several units: "5 units × 4 nights" with a dropdown for each unit
                            <StayUnitPrices
                                lines={priceDetails.lines}
                                nights={nights}
                                format={(value) => `${currencySymbol}${formatPrice(value)}`}
                            />
                        ) : (
                            priceDetails.lines.map((line) => (
                                <div className='tripPriceCal' key={line.unitId}>
                                    <p className='tripBookPrice'>
                                        {currencySymbol}
                                        {formatPrice(line.listPrice)} × {line.quantityLabel}
                                    </p>
                                    <p className='tripBookPrice'>
                                        {currencySymbol}
                                        {formatPrice(line.listBase)}
                                    </p>
                                </div>
                            ))
                        )}

                        {priceDetails.savings > 0 && (
                            <div className='tripBookOfferDiv'>
                                <p className='tripBookOfferText'>Discount</p>
                                <p className='tripBookOfferText'>
                                    - {currencySymbol}
                                    {formatPrice(priceDetails.savings)}
                                </p>
                            </div>
                        )}

                        {/* taxes are shown (and added) on the review page */}
                        <div className='tripBookTotalDiv'>
                            <p className='tripBookTotalText'>Total:</p>
                            <p className='tripBookTotalText'>
                                {currencySymbol}
                                {formatPrice(priceDetails.base)}
                            </p>
                        </div>
                        {priceDetails.tax > 0 && (
                            <p className='accommodationTaxNote'>Taxes are added on the review page</p>
                        )}
                    </div>
                    )}

                    {/* =================================================
                        BOOK BUTTON + HELP (same as the trip booking tab)
                    ================================================= */}

                    <div className='tripBookBtnDiv'>
                        <button
                            type='button'
                            className='tripButton tripBookingButton'
                            onClick={handleBookNow}
                            disabled={checkingAvailability}
                        >
                            {checkingAvailability
                                ? 'Checking availability…'
                                : isReserveChecked && hasReserveOption
                                ? `Reserve for ${currencySymbol}${formatPrice(reserveAmount)}`
                                : 'Book Now'}
                        </button>
                    </div>

                    <div className='helpSection'>
                        <div>Need Help?</div>
                        <div className='helpContact'>
                            <a href={`tel:+${HELP_PHONE}`} className='callBtn'>
                                <Phone className='helpIcons' />
                                Call
                            </a>
                            <a
                                className='chatBtn'
                                href={`https://wa.me/${HELP_PHONE}`}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <Message className='helpIcons' fill='black' />
                                Chat
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* =========================================================
                ALL UNITS MODAL
            ========================================================= */}

            {showUnits &&
                // rendered on <body> so the page's sticky tab bar can't cover it
                createPortal(
                <div
                    className='
                        accommodationUnitModalOverlay
                    '
                    onClick={() => setShowUnits(false)}
                >
                    <div
                        className='
                            accommodationUnitModal
                        '
                        onClick={(event) => event.stopPropagation()}
                    >
                        {/* HEADER */}

                        <div
                            className='
                                accommodationUnitModalHeader
                            '
                        >
                            <div>
                                <h2>All Accommodation Units</h2>

                                <p>
                                    {availability?.status === 'ready'
                                        ? `Availability for ${moment(availability.checkin).format('D MMM')} – ${moment(availability.checkout).format('D MMM')} · mix them as you like`
                                        : 'Choose one or more units — pick dates to see what’s free'}
                                </p>
                            </div>

                            <button type='button' onClick={() => setShowUnits(false)}>
                                <X size={22} />
                            </button>
                        </div>

                        {/* UNITS */}

                        <div
                            className='
                                accommodationUnitList
                            '
                        >
                            {units.map((unit, index) => {
                                const unitId = unit?._id || unit?.id || `unit-${index}`;

                                const unitPrice = getUnitPrice(unit);

                                const unitCurrency = getCurrencySymbol(unit?.pricing?.[0]?.currency);

                                const images = Array.isArray(unit?.images)
                                    ? unit.images.map(getImageUrl).filter(Boolean)
                                    : [];

                                const beds =
                                    unit?.beds?.reduce((total, bed) => total + Number(bed?.count || 0), 0) || 0;

                                const bathrooms = unit?.bathrooms?.count || 0;

                                const guests = unit?.occupancy?.total || unit?.maximumGuests || 0;

                                // free on the picked dates (null: no dates yet)
                                const left = leftFor(availability, unitId);
                                const soldOut = left === 0;

                                return (
                                    <div
                                        key={unitId}
                                        className='
                                                accommodationUnitModalItem
                                            '
                                    >
                                        {/* IMAGE */}

                                        <div
                                            className='
                                                    accommodationUnitModalImage
                                                '
                                        >
                                            {images.length > 0 ? (
                                                <img src={images[0]} alt={unit.name || 'Accommodation'} />
                                            ) : (
                                                <div>🏕️</div>
                                            )}
                                        </div>

                                        {/* INFO */}

                                        <div
                                            className='
                                                    accommodationUnitModalInfo
                                                '
                                        >
                                            <h3>{unit.name || 'Accommodation Unit'}</h3>

                                            {left !== null && (
                                                <span
                                                    className={`accommodationUnitLeft ${
                                                        soldOut ? 'soldOut' : left <= availability.fewLeft ? 'few' : ''
                                                    }`}
                                                >
                                                    {soldOut
                                                        ? 'Sold out for these dates'
                                                        : left <= availability.fewLeft
                                                          ? `Only ${left} left`
                                                          : `${left} available`}
                                                </span>
                                            )}

                                            <div
                                                className='
                                                        accommodationUnitFacts
                                                    '
                                            >
                                                <span>
                                                    <Users size={15} />

                                                    {guests}
                                                    {' Guests'}
                                                </span>

                                                <span>
                                                    <BedDouble size={15} />

                                                    {beds}
                                                    {' Beds'}
                                                </span>

                                                <span>
                                                    <Bath size={15} />

                                                    {bathrooms}
                                                    {' Bathroom'}
                                                </span>
                                            </div>

                                            {unit.description && <p>{unit.description}</p>}

                                            <div
                                                className='
                                                        accommodationUnitModalBottom
                                                    '
                                            >
                                                <strong>
                                                    {unitCurrency}
                                                    {formatPrice(unitPrice)}

                                                    {' / '}

                                                    {getPriceUnit(unit)}
                                                </strong>

                                                {selection[unitId] > 0 ? (
                                                    // − count + (rooms / tents of this unit)
                                                    <div className='accommodationUnitCounter'>
                                                        <button
                                                            type='button'
                                                            aria-label={`One less ${unit.name || 'unit'}`}
                                                            onClick={() => changeUnitRooms(unit, -1)}
                                                        >
                                                            −
                                                        </button>
                                                        <span>{selection[unitId]}</span>
                                                        <button
                                                            type='button'
                                                            aria-label={`One more ${unit.name || 'unit'}`}
                                                            disabled={selection[unitId] >= roomsLimit(unit)}
                                                            onClick={() => changeUnitRooms(unit, 1)}
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type='button'
                                                        disabled={soldOut}
                                                        onClick={() => changeUnitRooms(unit, 1)}
                                                    >
                                                        {soldOut ? 'Sold out' : 'Add'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* FOOTER: what's selected + Done */}
                        <div className='accommodationUnitModalFooter'>
                            <div>
                                <strong>{linesLabel(selectedLines)}</strong>
                                <span className={capacityShort ? 'short' : ''}>
                                    Fits {priceDetails.capacity} guests · {totalGuests} selected
                                </span>
                            </div>
                            <button type='button' onClick={() => setShowUnits(false)}>
                                Done
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
                )}
        </>
    );
}
